import React, { useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { Formik } from 'formik'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { startOfMonth, endOfMonth, startOfYear, endOfYear, startOfDay, endOfDay, isWithinInterval } from '@e3dc-react/shell/Libs/date'
import { saveAs } from 'file-saver'
import { CSVLink } from 'react-csv'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { Grid, Stack, Typography } from '@mui/material'
import { ButtonWithLoading } from '@e3dc-react/shell/Elements/Buttons'
import { makeStyles } from 'tss-react/mui'
import { Tabs } from '@e3dc-react/shell/Elements/Tabs'
import { TabsProps } from '@e3dc-react/shell/Elements/Tabs/Tabs'
import { exportChargingCardPdfApi } from '../api/export-charging-card.api'
import { generateChargingSessionsCsv } from '../../emobility-charging-sessions/utils/generate-charging-session-csv'
import ExportChargingCardDateRangeTab from './ExportChargingCardDateRangeTab'
import ExportChargingCardMonthYearTab from './ExportChargingCardMonthYearTab'
import { ChargingSessionCsvHeadersModel, ChargingSessionCsvDataModel } from 'src/Libs/e-mobility/charging-sessions/models/charging-session.model'
import { useChargingSessionsV1 } from 'src/Libs/e-mobility/charging-sessions'
import { handleReactNativeFileDownload } from '@e3dc-react/shell/Libs/webview'
import { base64ToArrayBuffer, CsvData, CsvHeaders, csvToArrayBuffer } from '@e3dc-react/shell/Libs/document.service'
import { useExportEmobilityChargingCardEditorFormInitializer } from 'src/Elements/P3_eMobility/emobility-charging-cards/hooks'
import { useIsWebviewMode } from 'src/Hooks'

export interface ExportChargingCardEditorProps {
  open: boolean
  onClose: () => void
  place: number
  rfid: string
  vehicleName: string
  chargingCard: string
  storageId: string
}

export interface ExportChargingCardFormData {
  month: Date | null
  year: Date | null
  from: Date | null
  to: Date | null
}

const useStyles = makeStyles()(theme => ({
  wrapper: {
    backgroundColor: theme.palette.shades.shade04,
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
  },
  text: {
    fontWeight: 500,
  },
  button: {
    marginRight: theme.spacing(1),
  },
}))

const ExportChargingCardEditor: React.FC<ExportChargingCardEditorProps> = ({ open, place, onClose, rfid, vehicleName, chargingCard, storageId }) => {
  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false)
  const [isCsvLoading, setIsCsvLoading] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<'month' | 'year' | 'dateRange'>('month')
  const [submitAction, setSubmitAction] = useState<'pdf' | 'csv'>('csv')
  const [csvData, setCsvData] = useState<{
    headers: ChargingSessionCsvHeadersModel[]
    data: ChargingSessionCsvDataModel[]
  }>({ headers: [], data: [] })
  const csvRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null)
  const { chargingSessions, isLoading: isChargingSessionsLoading } = useChargingSessionsV1(storageId)
  const sessions = chargingSessions ? chargingSessions.filter(item => item.authentication.rfid === rfid) : []
  const isWebview = useIsWebviewMode()
  const { classes } = useStyles()
  const authFetch = useAuthFetchWithResult()
  const { enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const { initialValues, validationSchema } = useExportEmobilityChargingCardEditorFormInitializer()

  const tabs: TabsProps['tabs'] = [
    {
      name: t('emobility.chargingCards.export.month'),
      content: <ExportChargingCardMonthYearTab field="month" />,
      onSelect: () => setActiveTab('month'),
    },
    {
      name: t('emobility.chargingCards.export.year'),
      content: <ExportChargingCardMonthYearTab field="year" />,
      onSelect: () => setActiveTab('year'),
    },
    {
      name: t('emobility.chargingCards.export.dateRange'),
      content: <ExportChargingCardDateRangeTab />,
      onSelect: () => setActiveTab('dateRange'),
    },
  ]

  const handleDateRange = (formData: ExportChargingCardFormData): { dateFrom: Date; dateTo: Date } => {
    let dateFrom: Date = new Date()
    let dateTo: Date = new Date()

    if (activeTab === 'month' && formData.month) {
      dateFrom = startOfMonth(formData.month)
      dateTo = endOfMonth(formData.month)
    }

    if (activeTab === 'year' && formData.year) {
      dateFrom = startOfYear(formData.year)
      dateTo = endOfYear(formData.year)
    }

    if (activeTab === 'dateRange' && formData.from && formData.to) {
      dateFrom = startOfDay(formData.from)
      dateTo = endOfDay(formData.to)
    }

    return { dateFrom, dateTo }
  }

  const handleExportPdf = async (dateFrom: Date, dateTo: Date): Promise<void> => {
    try {
      setIsPdfLoading(true)
      const response = await exportChargingCardPdfApi(dateFrom, dateTo, storageId, rfid, authFetch)
      if (isWebview) {
        handleReactNativeFileDownload(base64ToArrayBuffer(response), 'E3dc', 'pdf', 'application/pdf')
      } else {
        const blob = new Blob([Buffer.from(response, 'base64')], { type: 'application/pdf' })
        saveAs(blob, 'E3dc.pdf')
      }
    } catch {
      enqueueErrorStack(t('emobility.chargingCards.export.pdfFailed'))
    } finally {
      setIsPdfLoading(false)
    }
  }

  const handleSExportCsv = async (dateFrom: Date, dateTo: Date): Promise<void> => {
    if (!sessions) {
      return
    }
    try {
      setIsCsvLoading(true)
      const filteredSessions = sessions.filter(item => {
        return isWithinInterval(new Date(item.createdAt), { start: dateFrom, end: dateTo })
      })

      const { csvData, csvHeaders } = generateChargingSessionsCsv(filteredSessions, t)
      if (csvData.length > 0) {
        /**
         * Short explanation: flushSync solves a bug causing a corrupted CSV file to be downloaded(containing just some badly encoded symbols)
         *
         * "react-csv" package expects that the whole data set to be exported is known in advance. This data
         * is embedded into an html link element in the DOM. The library does not expose any hooks or utility functions
         * to export data on demand.
         *
         * Since date filters can be changes many times until the user actually decides to start the download, we do not
         * want to update the DOM until it is necessary(right before the download). flushSync callback makes sure that
         * the DOM is updated synchronously/immediately so that the click() function bound to the "react-csv" link in the
         * DOM contains sessions filtered by the latest date range value. Otherwise, the download occurs "halfway" during the
         * render and causes a corrupted file to be downloaded.
         *
         * Another solution to avoid flushSync here would be setting new CSV data after every date range change, but would
         * require a larger refactor of the functionality(which is to be replaced in the e-mobility V2 refactor using HagerBridge
         * backend anyway).
         **/
        flushSync(() => {
          setCsvData({ headers: csvHeaders, data: csvData })
        })
        if (isWebview) {
          const buffer = csvToArrayBuffer(csvData as unknown as CsvData, csvHeaders as unknown as CsvHeaders)
          handleReactNativeFileDownload(buffer, 'E3dc', 'csv', 'text/csv')
        } else {
          csvRef.current?.link.click()
        }
      } else {
        enqueueErrorStack(t('emobility.chargingCards.export.noData'))
      }
    } catch {
      enqueueErrorStack(t('emobility.chargingCards.export.csvFailed'))
    } finally {
      setIsCsvLoading(false)
    }
  }

  const handleSubmit = async (formData: ExportChargingCardFormData): Promise<void> => {
    const { dateFrom, dateTo } = handleDateRange(formData)
    if (submitAction === 'pdf') {
      handleExportPdf(dateFrom, dateTo)
    } else {
      handleSExportCsv(dateFrom, dateTo)
    }
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      {({ handleSubmit, isValid }) => {
        return (
          <Dialog
            maxWidth={'sm'}
            onClose={onClose}
            title={t('emobility.chargingCards.export.title')}
            place={place}
            actions={{
              secondary: {
                name: t('emobility.chargingCards.export.cancel'),
                onClick: onClose,
              },
            }}
            additionalActionsLeft={
              <Stack display={'flex'} flexDirection={'row'}>
                <ButtonWithLoading
                  /**
                   * Prevent user from clicking the "Export CSV" button and attempting to export a CSV file
                   * until all sessions are loaded(CSV file is built in FE from the charging session data)
                   *
                   * In practice, sessions will load a long time before the export dialog is open, so this
                   * is more of a safeguard than am impactful UX change.
                   **/
                  isLoading={isCsvLoading || isChargingSessionsLoading}
                  onClick={() => {
                    setSubmitAction('csv')
                    handleSubmit()
                  }}
                  disabled={!isValid || isChargingSessionsLoading}
                  label={t('emobility.chargingCards.export.csvExport')}
                  dataQa={'button-modal-primary'}
                  className={classes.button}
                />
                <CSVLink data={csvData.data} headers={csvData.headers} separator=";" filename={'E3dc.csv'} ref={csvRef} />
                <ButtonWithLoading
                  isLoading={isPdfLoading}
                  onClick={() => {
                    setSubmitAction('pdf')
                    handleSubmit()
                  }}
                  disabled={!isValid}
                  label={t('emobility.chargingCards.export.pdfExport')}
                  dataQa={'button-modal-primary'}
                />
              </Stack>
            }
          >
            <Stack my={3} className={classes.wrapper}>
              <Grid container>
                <Grid item xs={6}>
                  <Typography>{t('emobility.chargingCards.chargingCard')}</Typography>
                  <Typography className={classes.text}>
                    {chargingCard} {`(RFID: ${rfid})`}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography>{t('emobility.chargingCards.vehicle')}</Typography>
                  <Typography className={classes.text}>{vehicleName}</Typography>
                </Grid>
              </Grid>
            </Stack>
            <Tabs tabs={tabs} />
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default ExportChargingCardEditor
